import type { EditorJsData } from '../editorjs-types';
import type { EditorJsEditorHandle } from './viewer/editor';

import { createContext, useContext, useMemo, type ReactNode } from 'react';

export interface EditDocumentContextValue {
  /** Ref to the Editor.js editor instance */
  editorRef: React.RefObject<EditorJsEditorHandle | null>;
  /** Replace the entire document content (e.g. after AI edit) */
  setValue: (value: EditorJsData | null) => void;
  /** Get current value from the editor */
  getValue: () => EditorJsData | null;
  /** Flush Editor.js saver into the value ref (call before persisting). */
  flushSave: () => Promise<void>;
}

const EditDocumentContext = createContext<EditDocumentContextValue | undefined>(
  undefined,
);

export interface EditDocumentProviderProps {
  editorRef: React.RefObject<EditorJsEditorHandle | null>;
  children: ReactNode;
}

export function EditDocumentProvider({
  editorRef,
  children,
}: EditDocumentProviderProps) {
  const value = useMemo<EditDocumentContextValue>(
    () => ({
      editorRef,
      setValue: (value: EditorJsData | null) => {
        editorRef.current?.resetContent(value);
      },
      getValue: () => editorRef.current?.getValue() ?? null,
      flushSave: () => editorRef.current?.flushSave() ?? Promise.resolve(),
    }),
    [editorRef],
  );

  return (
    <EditDocumentContext.Provider value={value}>
      {children}
    </EditDocumentContext.Provider>
  );
}

export function useEditDocument() {
  const context = useContext(EditDocumentContext);
  if (!context) {
    throw new Error('useEditDocument must be used within EditDocumentProvider');
  }

  return context;
}
